import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFileExcel,
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaDownload,
  FaChevronDown,
  FaChevronUp,
  FaCheck,
  FaTimes,
  FaHistory,
} from 'react-icons/fa';
import { previewJobScheduleImport, executeJobScheduleImport, saveJobScheduleImportHistory } from '../services/utilityService';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
  if (val === null || val === undefined) return '—';
  return `$${Number(val).toFixed(2)}`;
};

const formatFrequency = (frequencyType, frequencyValue) => {
  if (!frequencyType) return '—';
  if (frequencyType === 'monthly') return 'Monthly';
  return `Every ${frequencyValue || ''} ${frequencyType}`.trim();
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const SummaryCard = ({ icon: Icon, label, value, colorClass }) => (
  <div className={`bg-white rounded-lg border p-4 flex items-center gap-4 shadow-sm ${colorClass}`}>
    <Icon className="text-3xl" />
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const ValidSchedulesTable = ({ schedules, selectedIndices, onToggle, onSelectAll, onSelectNone }) => {
  const [collapsed, setCollapsed] = useState(false);
  const allSelected = schedules.length > 0 && schedules.every((s) => selectedIndices.has(s.rowIndex));

  return (
    <div className="bg-white rounded-lg border border-green-200 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-green-50 cursor-pointer"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2 font-semibold text-green-800">
          <FaCheckCircle className="text-green-500" />
          Valid Schedules ({schedules.length})
        </div>
        <div className="flex items-center gap-3">
          <button
            className="text-xs underline text-green-700 hover:text-green-900"
            onClick={(e) => { e.stopPropagation(); onSelectAll(); }}
          >
            Select All
          </button>
          <button
            className="text-xs underline text-green-700 hover:text-green-900"
            onClick={(e) => { e.stopPropagation(); onSelectNone(); }}
          >
            Select None
          </button>
          {collapsed ? <FaChevronDown className="text-green-600" /> : <FaChevronUp className="text-green-600" />}
        </div>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm divide-y divide-gray-100">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-center w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => e.target.checked ? onSelectAll() : onSelectNone()}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-3 py-2 text-left">Row</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">FSE Code</th>
                <th className="px-3 py-2 text-left">FSE Name</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Frequency</th>
                <th className="px-3 py-2 text-right">FSE $</th>
                <th className="px-3 py-2 text-right">Inv $</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedules.map((s, idx) => {
                const isSelected = selectedIndices.has(s.rowIndex);
                return (
                  <tr
                    key={s.rowIndex}
                    className={`cursor-pointer transition-colors ${isSelected ? 'bg-green-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50`}
                    onClick={() => onToggle(s.rowIndex)}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggle(s.rowIndex)}
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-500">{s.rowIndex}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{s.clientName}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-xs" title={s.address}>
                      <div className="truncate">{s.householdName || '—'}</div>
                      {s.address && (
                        <div className="text-xs text-gray-400 truncate">{s.address}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{s.fseCode}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-xs" title={s.franchiseeName}>
                      <div className="truncate">{s.franchiseeName || '—'}</div>
                      {s.franchiseePersonName && (
                        <div className="text-xs text-gray-400 truncate">{s.franchiseePersonName}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{s.serviceType}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {formatFrequency(s.frequencyType, s.frequencyValue)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(s.fsePrice)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(s.invoicePrice)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Normalize the full warning message to a short group label (strips dynamic parts like IDs)
const getWarningTypeLabel = (warning = '') => {
  if (warning.includes('already exists')) return 'Duplicate schedule — same franchisee, client, service & frequency';
  if (warning.includes('Multiple households')) return 'Multiple households found for address';
  return 'Warning';
};

const WarnedSchedulesTable = ({ schedules, selectedIndices, onToggle, onSelectAll, onSelectNone }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [openWarningRow, setOpenWarningRow] = useState(null);
  if (!schedules.length) return null;

  // Group by normalized type label (not the full message with IDs)
  const groups = schedules.reduce((acc, s) => {
    const key = getWarningTypeLabel(s.warning);
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const COL_COUNT = 11; // checkbox + row + supplier + client + fse code + fse name + service + frequency + fse$ + inv$ + icon

  const tableHeaders = (
    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
      <tr>
        <th className="px-3 py-2 text-center w-10" />
        <th className="px-3 py-2 text-left">Row</th>
        <th className="px-3 py-2 text-left">Supplier</th>
        <th className="px-3 py-2 text-left">Client</th>
        <th className="px-3 py-2 text-left">FSE Code</th>
        <th className="px-3 py-2 text-left">FSE Name</th>
        <th className="px-3 py-2 text-left">Service</th>
        <th className="px-3 py-2 text-left">Frequency</th>
        <th className="px-3 py-2 text-right">FSE $</th>
        <th className="px-3 py-2 text-right">Inv $</th>
        <th className="px-3 py-2 w-8" />
      </tr>
    </thead>
  );

  return (
    <div className="bg-white rounded-lg border border-yellow-300 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-yellow-50 cursor-pointer"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2 font-semibold text-yellow-800">
          <FaExclamationTriangle className="text-yellow-500" />
          With Warnings — Review Before Importing ({schedules.length})
        </div>
        <div className="flex items-center gap-3">
          <button
            className="text-xs underline text-yellow-700 hover:text-yellow-900"
            onClick={(e) => { e.stopPropagation(); onSelectAll(); }}
          >
            Select All
          </button>
          <button
            className="text-xs underline text-yellow-700 hover:text-yellow-900"
            onClick={(e) => { e.stopPropagation(); onSelectNone(); }}
          >
            Select None
          </button>
          {collapsed ? <FaChevronDown className="text-yellow-600" /> : <FaChevronUp className="text-yellow-600" />}
        </div>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          {Object.entries(groups).map(([label, group]) => (
            <div key={label}>
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 border-t border-yellow-200 text-yellow-800 text-xs font-semibold">
                {/* <FaExclamationTriangle className="text-yellow-500 flex-shrink-0" /> */}
                {label}
              </div>
              <table className="min-w-full text-sm divide-y divide-gray-100">
                {tableHeaders}
                <tbody className="divide-y divide-gray-100">
                  {group.map((s, idx) => {
                    const isSelected = selectedIndices.has(s.rowIndex);
                    const isOpen = openWarningRow === s.rowIndex;
                    return (
                      <React.Fragment key={s.rowIndex}>
                        <tr
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-yellow-100' : idx % 2 === 0 ? 'bg-yellow-50' : 'bg-white'} hover:bg-yellow-100`}
                          onClick={() => onToggle(s.rowIndex)}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => onToggle(s.rowIndex)}
                              onClick={(e) => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-500">{s.rowIndex}</td>
                          <td className="px-3 py-2 font-medium text-gray-800">{s.clientName}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs" title={s.address}>
                            <div className="truncate">{s.householdName || '—'}</div>
                            {s.address && (
                              <div className="text-xs text-gray-400 truncate">{s.address}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{s.fseCode}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs" title={s.franchiseeName}>
                            <div className="truncate">{s.franchiseeName || '—'}</div>
                            {s.franchiseePersonName && (
                              <div className="text-xs text-gray-400 truncate">{s.franchiseePersonName}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{s.serviceType}</td>
                          <td className="px-3 py-2 text-gray-600">
                            {formatFrequency(s.frequencyType, s.frequencyValue)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(s.fsePrice)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(s.invoicePrice)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              title="Show warning detail"
                              onClick={(e) => { e.stopPropagation(); setOpenWarningRow(isOpen ? null : s.rowIndex); }}
                              className={`text-yellow-500 hover:text-yellow-700 transition-colors ${isOpen ? 'text-yellow-700' : ''}`}
                            >
                              <FaExclamationTriangle />
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-yellow-50">
                            <td colSpan={COL_COUNT} className="px-4 py-2 text-xs text-yellow-800 border-b border-yellow-200">
                              {s.warning}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InvalidSchedulesTable = ({ schedules }) => {
  const [collapsed, setCollapsed] = useState(false);
  if (!schedules.length) return null;

  return (
    <div className="bg-white rounded-lg border border-red-200 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-red-50 cursor-pointer"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2 font-semibold text-red-800">
          <FaTimesCircle className="text-red-500" />
          Invalid Rows — Will Not Be Imported ({schedules.length})
        </div>
        {collapsed ? <FaChevronDown className="text-red-600" /> : <FaChevronUp className="text-red-600" />}
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm divide-y divide-gray-100">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Row</th>
                <th className="px-3 py-2 text-left">Job</th>
                <th className="px-3 py-2 text-left">FSE Code</th>
                <th className="px-3 py-2 text-left">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedules.map((s, idx) => (
                <tr key={s.rowIndex} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 text-gray-500">{s.rowIndex}</td>
                  <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={s.rawData?.Job}>{s.rawData?.Job || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{s.rawData?.['FSE Code'] || '—'}</td>
                  <td className="px-3 py-2 text-red-600 font-medium">{s.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const ImportResults = ({ results, onReset, isHistoryView = false, onBackToHistory }) => {
  const { imported, failed, results: details = [], dryRun } = results;
  const succeeded = details.filter((d) => d.status === 'success');
  const errored = details.filter((d) => d.status !== 'success');

  const download = () => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-schedule-import-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const ResultTable = ({ rows, success }) => {
    if (!rows.length) return null;
    return (
      <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${success ? 'border-green-200' : 'border-red-200'}`}>
        <div className={`flex items-center gap-2 px-4 py-3 font-semibold ${success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {success ? <FaCheckCircle className="text-green-500" /> : <FaTimesCircle className="text-red-500" />}
          {success ? `Successfully Imported (${rows.length})` : `Failed (${rows.length})`}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm divide-y divide-gray-100">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2 text-left">Row</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">FSE Code</th>
                <th className="px-3 py-2 text-left">FSE Name</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Frequency</th>
                <th className="px-3 py-2 text-right">FSE $</th>
                <th className="px-3 py-2 text-right">Inv $</th>
                {!success && <th className="px-3 py-2 text-left">Reason</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((d, idx) => (
                <tr key={d.rowIndex} className={idx % 2 === 0 ? (success ? 'bg-green-50' : 'bg-red-50') : 'bg-white'}>
                  <td className="px-3 py-2 text-center">
                    {success
                      ? <FaCheck className="text-green-500 mx-auto" />
                      : <FaTimes className="text-red-500 mx-auto" />}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{d.rowIndex}</td>
                  <td className="px-3 py-2 font-medium">
                    {success && d.scheduleId
                      ? <Link to={`/job-scheduler/edit/${d.scheduleId}`} className="text-jims-cyan hover:underline" onClick={(e) => e.stopPropagation()}>{d.clientName || '—'}</Link>
                      : <span className="text-gray-800">{d.clientName || '—'}</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-xs" title={d.address}>
                    <div className="truncate">{d.householdName || '—'}</div>
                    {d.address && (
                      <div className="text-xs text-gray-400 truncate">{d.address}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{d.fseCode || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-xs">
                    <div className="truncate">{d.franchiseeName || '—'}</div>
                    {d.franchiseePersonName && (
                      <div className="text-xs text-gray-400 truncate">{d.franchiseePersonName}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{d.serviceType || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{formatFrequency(d.frequencyType, d.frequencyValue)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(d.fsePrice)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(d.invoicePrice)}</td>
                  {!success && <td className="px-3 py-2 text-red-600 text-xs">{d.message || '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {dryRun
            ? <SummaryCard icon={FaExclamationTriangle} label="Dry Run — No changes made" value={results.totalSchedules} colorClass="border-yellow-200 text-yellow-700" />
            : (
              <>
                <SummaryCard icon={FaCheckCircle} label="Successfully Imported" value={imported} colorClass="border-green-200 text-green-700" />
                <SummaryCard icon={FaTimesCircle} label="Failed" value={failed} colorClass="border-red-200 text-red-700" />
              </>
            )}
        </div>
        <div className="flex items-center gap-3 text-sm divide-x divide-gray-300">
          {!isHistoryView && (
            <button onClick={onReset} className="pl-3 text-jims-cyan hover:text-jims-cyan-dark font-medium">
              New Import
            </button>
          )}
          {isHistoryView && (
            <button onClick={onBackToHistory} className="pl-3 text-gray-500 hover:text-gray-700 font-medium">
              ← Back to History
            </button>
          )}
          {!isHistoryView && (
            <Link to="/utilities/import-job-schedules/history" className="pl-3 text-jims-cyan hover:text-jims-cyan-dark font-medium inline-flex items-center gap-1">
              <FaHistory className="h-3 w-3" /> View Import History
            </Link>
          )}
          <Link to="/job-scheduler" className="pl-3 text-jims-cyan hover:text-jims-cyan-dark font-medium">
            Job Schedules →
          </Link>
        </div>
      </div>

      <ResultTable rows={succeeded} success={true} />
      <ResultTable rows={errored} success={false} />

      <div>
        <button onClick={download} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition-colors text-sm">
          <FaDownload /> Download Report
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'importJobSchedules_lastResults';

const ImportJobSchedulesUtility = ({ onBack }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Restore results from sessionStorage if available
  const savedSession = (() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)); } catch { return null; }
  })();

  // Stage: 'upload' | 'preview' | 'results'
  const [stage, setStage] = useState(savedSession ? 'results' : 'upload');

  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Selection state: Set of rowIndex values
  const [selectedIndices, setSelectedIndices] = useState(new Set());

  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResults, setImportResults] = useState(savedSession || null);

  // ── File handling ──

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      setParseError('Only .xlsx files are supported.');
      return;
    }
    setSelectedFile(file);
    setParseError(null);
  }, []);

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  // ── Parse / Preview ──

  const handleParseFile = async () => {
    if (!selectedFile) return;
    setIsParsing(true);
    setParseError(null);

    try {
      const response = await previewJobScheduleImport(selectedFile);
      const data = response.data;
      setPreviewData(data);
      // Pre-select only schedules that have no duplicate warning
      setSelectedIndices(new Set(data.validSchedules.filter((s) => !s.warning).map((s) => s.rowIndex)));
      setStage('preview');
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
    } finally {
      setIsParsing(false);
    }
  };

  // ── Selection ──

  const toggleSelection = useCallback((rowIndex) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (!previewData) return;
    setSelectedIndices(new Set(previewData.validSchedules.map((s) => s.rowIndex)));
  }, [previewData]);

  const selectNone = useCallback(() => {
    setSelectedIndices(new Set());
  }, []);

  // ── Import ──

  const handleImport = async () => {
    if (selectedIndices.size === 0) return;
    setIsImporting(true);
    setImportError(null);

    // Build list of selected schedules with their payloads
    const schedulesToImport = previewData.validSchedules
      .filter((s) => selectedIndices.has(s.rowIndex))
      .map((s) => ({
        rowIndex: s.rowIndex,
        payload: s.payload,
        clientName: s.clientName,
        address: s.address,
        serviceType: s.serviceType,
        scheduleType: s.scheduleType,
      }));

    try {
      const response = await executeJobScheduleImport(schedulesToImport, false);
      // Enrich each result row with the full schedule info from preview
      const scheduleByRow = Object.fromEntries(
        previewData.validSchedules.map((s) => [s.rowIndex, s])
      );
      const enrichedDetails = (response.data.results || []).map((r) => ({
        ...scheduleByRow[r.rowIndex],
        ...r,
      }));
      const finalResults = { ...response.data, results: enrichedDetails };
      setImportResults(finalResults);
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(finalResults)); } catch {}
      // Save to Firestore history (non-blocking — don't fail the UI if this errors)
      saveJobScheduleImportHistory({
        fileName: selectedFile?.name || 'unknown.xlsx',
        summary: { total: schedulesToImport.length, imported: finalResults.imported || 0, failed: finalResults.failed || 0 },
        results: enrichedDetails,
      }).catch((err) => console.warn('[IMPORT-HISTORY] Failed to save history:', err));
      setStage('results');
    } catch (err) {
      setImportError(err.message || 'Import failed.');
    } finally {
      setIsImporting(false);
    }
  };

  // ── Reset ──

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setSelectedIndices(new Set());
    setImportResults(null);
    setParseError(null);
    setImportError(null);
    setStage('upload');
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Render ──

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FaFileExcel className="text-green-600" /> Import Job Schedules
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel file to preview and selectively import job schedules.
          </p>
        </div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 underline">
          ← Back to Utilities
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 px-4 sm:px-0 text-sm">
        {['Upload', 'Preview', 'Results'].map((label, i) => {
          const stepStage = ['upload', 'preview', 'results'][i];
          const active = stage === stepStage;
          const done = ['upload', 'preview', 'results'].indexOf(stage) > i;
          return (
            <React.Fragment key={label}>
              <span className={`font-medium px-3 py-1 rounded-full ${active ? 'bg-jims-green text-white' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {done ? <FaCheck className="inline mr-1" /> : null}{label}
              </span>
              {i < 2 && <span className="text-gray-300">›</span>}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── STAGE: Upload ── */}
      {stage === 'upload' && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-4 mx-4 sm:mx-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">Select XLSX File</h2>
            <Link to="/utilities/import-job-schedules/history" className="text-sm text-jims-cyan hover:text-jims-cyan-dark font-medium inline-flex items-center gap-1">
              <FaHistory className="h-3 w-3" /> View Import History
            </Link>
          </div>

          {/* Dropzone */}
          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-jims-green bg-green-50' : 'border-gray-300 hover:border-jims-green hover:bg-green-50'}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FaFileExcel className="text-5xl text-green-500 mx-auto mb-3" />
            {selectedFile ? (
              <div>
                <p className="font-semibold text-gray-800">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB — click to change</p>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 font-medium">Drop your XLSX file here or click to browse</p>
                <p className="text-sm text-gray-400 mt-1">Excel 2007+ (.xlsx) files only, max 20 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>

          {parseError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded flex items-start gap-2">
              <FaTimesCircle className="mt-0.5 flex-shrink-0" />
              <span>{parseError}</span>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleParseFile}
              disabled={!selectedFile || isParsing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-jims-cyan text-white font-semibold rounded-md hover:bg-jims-cyan-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isParsing ? <FaSpinner className="animate-spin" /> : <FaUpload />}
              {isParsing ? 'Parsing...' : 'Parse & Preview'}
            </button>
          </div>
        </div>
      )}

      {/* ── STAGE: Preview ── */}
      {stage === 'preview' && previewData && (() => {
        const cleanSchedules = previewData.validSchedules.filter((s) => !s.warning);
        const warnedSchedules = previewData.validSchedules.filter((s) => !!s.warning);
        const selectAllClean = () => setSelectedIndices((prev) => new Set([...prev, ...cleanSchedules.map((s) => s.rowIndex)]));
        const selectNoneClean = () => setSelectedIndices((prev) => { const next = new Set(prev); cleanSchedules.forEach((s) => next.delete(s.rowIndex)); return next; });
        const selectAllWarned = () => setSelectedIndices((prev) => new Set([...prev, ...warnedSchedules.map((s) => s.rowIndex)]));
        const selectNoneWarned = () => setSelectedIndices((prev) => { const next = new Set(prev); warnedSchedules.forEach((s) => next.delete(s.rowIndex)); return next; });
        return (
        <div className="space-y-5 mx-4 sm:mx-0">
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SummaryCard icon={FaFileExcel} label="Total Rows" value={previewData.totalRows} colorClass="border-gray-200 text-gray-700" />
            <SummaryCard icon={FaCheckCircle} label="Valid" value={cleanSchedules.length} colorClass="border-green-200 text-green-700" />
            <SummaryCard icon={FaTimesCircle} label="Invalid" value={previewData.invalidSchedules.length} colorClass="border-red-200 text-red-700" />
            <SummaryCard icon={FaExclamationTriangle} label="With Warnings" value={warnedSchedules.length} colorClass="border-yellow-200 text-yellow-700" />
          </div>

          {/* Actions — shown above the tables so always visible */}
          {previewData.validSchedules.length > 0 && (
            <div className="flex items-center gap-3 py-2">
              <button
                onClick={handleImport}
                disabled={selectedIndices.size === 0 || isImporting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-jims-cyan text-white font-semibold rounded-md hover:bg-jims-cyan-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isImporting ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                {isImporting
                  ? 'Importing...'
                  : `Import ${selectedIndices.size} Selected Schedule${selectedIndices.size !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {importError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded flex items-start gap-2">
              <FaTimesCircle className="mt-0.5 flex-shrink-0" />
              <span>{importError}</span>
            </div>
          )}

          {/* Clean valid schedules table */}
          {cleanSchedules.length > 0 && (
            <ValidSchedulesTable
              schedules={cleanSchedules}
              selectedIndices={selectedIndices}
              onToggle={toggleSelection}
              onSelectAll={selectAllClean}
              onSelectNone={selectNoneClean}
            />
          )}

          {/* Warned schedules table */}
          <WarnedSchedulesTable
            schedules={warnedSchedules}
            selectedIndices={selectedIndices}
            onToggle={toggleSelection}
            onSelectAll={selectAllWarned}
            onSelectNone={selectNoneWarned}
          />

          {/* Invalid schedules table */}
          <InvalidSchedulesTable schedules={previewData.invalidSchedules} />

          {/* No valid or warned schedules */}
          {previewData.validSchedules.length === 0 && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded">
              No valid schedules were found in this file. Please review the invalid rows above.
            </div>
          )}

          {previewData.validSchedules.length === 0 && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition-colors"
            >
              Start Over
            </button>
          )}
        </div>
        );
      })()}

      {/* ── STAGE: Results ── */}
      {stage === 'results' && importResults && (
        <div className="mx-4 sm:mx-0">
          <ImportResults results={importResults} onReset={handleReset} />
        </div>
      )}
    </div>
  );
};

export default ImportJobSchedulesUtility;
